import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import { useClickOutside } from "hooks/useClickOutside";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useChallengesState } from "hooks/useChallengesState";
import { CHALLENGE_STATUS } from 'pages/dashboard/challenges/state/constants';

const SelectOppRisk = ({ categoryId, instanceId }) => {
  const [selectedTab, setSelectedTab] = useState("opportunities");
  const [selectedContent, setSelectedContent] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const selectedContentRef = useRef(null);
  const dropdownRef = useRef(null);

  const {
    state,
    addDraftOpportunity,
    addDraftRisk,
    updateDraftOpportunity,
    updateDraftRisk,
    deleteDraftOpportunity,
    deleteDraftRisk,
    activateDraftOpportunity,
    activateDraftRisk,
    setActiveItemType
  } = useChallengesState();

  // Get the instance and its attached draft
  const instance = state.challenges.instances[instanceId];
  const isDraft = instance?.status === CHALLENGE_STATUS.DRAFT;
  const opportunities = isDraft ? instance?.data?.opportunities || [] : [];
  const risks = isDraft ? instance?.data?.risks || [] : [];
  const category = state.challenges.categories[categoryId];

  // Get the current items based on selected tab with proper null checks
  const items = opportunities.concat(risks);
  const activeItem = items?.find?.(item => item?.is_active) || null;
  const activeItemId = activeItem?.id;

  useEffect(() => {
    if (isDraft) {
      setActiveItemType(categoryId, null, selectedTab);
    }
  }, [selectedTab, isDraft]);

  useClickOutside(dropdownRef, (e) => {
    // Don't close if clicking the expand button or privacy icon
    if (e.target.closest('.dark') || e.target.closest('.PrivacyTipIcon')) return;
    setIsExpanded(false);
    selectedContentRef.current?.blur();
  });

  const handleTabClick = (tabName) => {
    setSelectedTab(tabName);
    // Only clear content if there's no active item in the current tab
    const currentItems = opportunities.concat(risks);
    const currentActiveItem = currentItems?.find?.(item => item?.is_active);
    setSelectedContent(currentActiveItem?.content || '');
    // Only focus if not already expanded
    if (!isExpanded) {
      selectedContentRef.current?.focus();
    }
  };

  const handleContentChange = (e) => {
    setSelectedContent(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && selectedContent.trim() && isDraft) {
      if (activeItem) {
        // Update existing item
        if (selectedTab === "opportunities") {
          updateDraftOpportunity(categoryId, null, activeItem.id, selectedContent.trim());
        } else {
          updateDraftRisk(categoryId, null, activeItem.id, selectedContent.trim());
        }
      } else {
        // Only create new item if no active item
        if (selectedTab === "opportunities") {
          addDraftOpportunity(categoryId, null, selectedContent.trim());
        } else {
          addDraftRisk(categoryId, null, selectedContent.trim());
        }
      }
      setSelectedContent('');
      setIsExpanded(true);
      selectedContentRef.current?.focus();
    }
  };

  const handleAddNew = () => {
    if (activeItem) {
      if (selectedTab === "opportunities") {
        activateDraftOpportunity(categoryId, null, null);
      } else {
        activateDraftRisk(categoryId, null, null);
      }
    }
    setSelectedContent('');
    selectedContentRef.current?.focus();
  };

  const handleItemClick = (item) => {
    if (isDraft) {
      if (selectedTab === "opportunities") {
        activateDraftOpportunity(categoryId, null, item.id);
      } else {
        activateDraftRisk(categoryId, null, item.id);
      }
      setSelectedContent(item.content);
      setIsExpanded(false);
    }
  };

  const handleDeleteItem = (e, itemId) => {
    e.stopPropagation();
    if (isDraft) {
      if (selectedTab === "opportunities") {
        deleteDraftOpportunity(categoryId, null, itemId);
      } else {
        deleteDraftRisk(categoryId, null, itemId);
      }
    }
  };

  const handleFocus = () => {
    if (!activeItemId && isDraft) {
      setIsExpanded(true);
    }
  };

  const handleExpandClick = () => {
    if (!isDraft) return;
    const newState = !isExpanded;
    setIsExpanded(newState);
    if (!newState) {
      selectedContentRef.current?.blur();
    }
  };

  useEffect(() => {
    if (isDraft && activeItem) {
      setSelectedContent(activeItem.content);
    }
  }, [activeItem?.id, isDraft]);

  // Disable the component if no draft is attached
  if (!isDraft) {
    return (
      <OppRiskContainer>
        <div ref={dropdownRef}>
          <Selectors>
            <Tabs>
              <Tab $active={false} $disabled>
                <StatusDot type="green" />
                <TabLabel>Opportunities</TabLabel>
              </Tab>
              <Tab $active={false} $disabled>
                <StatusDot type="red" />
                <TabLabel>Risks</TabLabel>
              </Tab>
            </Tabs>
          </Selectors>
          <Content>
            <InputWrapper>
              <SelectedContent
                disabled
                placeholder="Attach a challenge to add opportunities and risks..."
              />
            </InputWrapper>
          </Content>
        </div>
      </OppRiskContainer>
    );
  }

  return (
    <OppRiskContainer>
      <div ref={dropdownRef}>
        <Selectors>
          <Tabs>
            <Tab
              $active={selectedTab === "opportunities"}
              onClick={() => handleTabClick("opportunities")}
            >
              <StatusDot type="green" />
              <TabLabel>Opportunities</TabLabel>
            </Tab>
            <Tab
              $active={selectedTab === "risks"}
              onClick={() => handleTabClick("risks")}
            >
              <StatusDot type="red" />
              <TabLabel>Risks</TabLabel>
            </Tab>
          </Tabs>
        </Selectors>
        <Content>
          <InputWrapper>
            <SelectedContent 
              ref={selectedContentRef}
              value={selectedContent}
              onChange={handleContentChange}
              onKeyPress={handleKeyPress}
              onFocus={handleFocus}
              placeholder={`Brainstorm ${selectedTab === "opportunities" ? "opportunities" : "risks"} & choose one to work on.`}
            />
            <ExpandArrow 
              $isExpanded={isExpanded} 
              onClick={handleExpandClick}
              $hasActiveItem={!!activeItemId}
              data-testid="expand-arrow"
            >
              <KeyboardArrowDownIcon />
            </ExpandArrow>
          </InputWrapper>
          <DropdownContent $isExpanded={isExpanded}>
            {activeItemId && selectedContent.trim() && (
              <AddNewButton onClick={handleAddNew}>
                Add New {selectedTab === "opportunities" ? "Opportunity?" : "Risk?"}
              </AddNewButton>
            )}
         
            {[...items].reverse().map((item) => (
              <DropdownItem 
                key={item.id}
                isActive={item.id === activeItemId}
                onClick={() => handleItemClick(item)}
              >
                <ItemContent>{item.content}</ItemContent>
                {item.id === activeItemId ? (
                  <ActivateLink onClick={(e) => {
                    e.stopPropagation();
                    if (selectedTab === "opportunities") {
                      activateDraftOpportunity(categoryId, null, null);
                    } else {
                      activateDraftRisk(categoryId, null, null);
                    }
                    setSelectedContent('');
                  }}>
                    Deactivate
                  </ActivateLink>
                ) : (
                  <ActivateLink>
                    Activate?
                  </ActivateLink>
                )}
                <DeleteItemButton 
                  onClick={(e) => handleDeleteItem(e, item.id)}
                  title="Delete item"
                >
                  ×
                </DeleteItemButton>
              </DropdownItem>
            ))}
            {items.length === 0 && (
              <EmptyState>
                Brainstorm {selectedTab === "opportunities" ? "opportunities" : "risks"}, then select one to work on.
              </EmptyState>
            )}
          </DropdownContent>
        </Content>
      </div>
    </OppRiskContainer>
  );
};

// Styled components from BottomRow.js
const OppRiskContainer = styled.div.attrs({
  className: "OppRiskContainer",
})`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 10px 0px 10px 0px;
  background: #e2e2e2;
  margin: 5px 0px 0px;
  border-radius: 3px 3px 0px 0px;
  position: relative;
  z-index: 2;
  opacity: 0;
  animation: fadeIn 0.2s ease-in forwards;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const Selectors = styled.div.attrs({
  className: "Selectors",
})`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 20px;
`;

const Tabs = styled.div.attrs({
  className: "Tabs",
})`
  display: flex;
  gap: 8px;
  font-size: 12px;
  padding: 0px 0px 0px 10px;
  margin-bottom: 5px;
`;

const Tab = styled.div.attrs({
  className: "Tab",
})`
  border-radius: 3px;
  padding: 8px 15px 8px 10px;
  background: ${(props) => (props.$active ? "black" : "#e5e5e5")};
  color: ${(props) => (props.$active ? "white" : "black")};
  opacity: ${props => props.$disabled ? 0.5 : 1};
  transition: all 0.2s;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  gap: 5px;
  &:hover {
    ${(props) =>
    !props.$active && !props.$disabled &&
    `
      background: #dedede;
      color: black;
    `}
  }
`;

const TabLabel = styled.span.attrs({
  className: "TabLabel",
})`
  font-weight: 700;
  margin-top: -2px;
`;

const Content = styled.div.attrs({
  className: "Content",
})`
  flex: 1;
  display: flex;
  position: relative;
  flex-direction: column;
  background: white;
  margin: 0px 10px;
  box-sizing: border-box;
  border-radius: 3px;
  margin-top: 5px;
`;

const InputWrapper = styled.div.attrs({
  className: "InputWrapper",
})`
  display: flex;
  align-items: center;
  position: relative;
  background: #f1f1f1;
  border-radius: 3px;
  padding-right: 10px;
`;

const ExpandArrow = styled.div.attrs({
  className: "ExpandArrow",
})`
  color: ${props => props.$hasActiveItem ? '#0086d0' : '#666'};
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  transform: ${props => props.$isExpanded ? 'rotate(180deg)' : 'rotate(0)'};
  &:hover {
    color: ${props => props.$hasActiveItem ? '#017cbf' : '#333'};
  }
  
  svg {
    font-size: 21px;
  }
`;

const SelectedContent = styled.input.attrs({
  className: "SelectedContent",
})`
  flex: 1;
  padding: 0px;
  background: transparent;
  margin: 0px 10px;
  min-height: 30px;
  border: none;
  outline: none;
  font-size: 13px;
  padding-left: 0px;
  font-family: PT Sans;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background: transparent;
  }
`;

const DropdownContent = styled.div.attrs({
  className: "DropdownContent",
})`
  position: absolute;
  top: 40px;
  left: 0;
  right: 0;
  box-sizing: border-box;
  padding: 10px 10px 10px 10px;
  background: white;
  gap: 2px;
  display: flex;
  flex-direction: column;
  opacity: ${(props) => (props.$isExpanded ? 1 : 0)};
  visibility: ${(props) => (props.$isExpanded ? "visible" : "hidden")};
  z-index: 5;
  margin: 0px -10px;
  border-radius: 0px 0px 3px 3px;
  overflow-y: scroll;
  height: 500%;
  font-family: PT Sans;
  transition: all 0.2s ease-in-out;
`;

const DropdownItem = styled.div.attrs({
  className: "DropdownItem",
})`
  border-radius: 2px;
  padding: 5px 8px;
  font-size: 13px;
  color: black;
  font-weight: 400;
  background: ${props => props.isActive ? '#e6f3ff' : 'white'};
  border: none;
  outline: none;
  cursor: pointer;
  font-family: PT Sans;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: fit-content;
  &:hover {
    background: ${props => props.isActive ? '#d9ecff' : '#f5f5f5'};
  }
`;

const ActivateLink = styled.span.attrs({
  className: 'ActivateLink'
})`
  color: #0086d0;
  font-size: 12px;
  margin-left: 10px;
  cursor: pointer;
  font-weight: 700;
  text-decoration: underline;
  margin-left: 80px;
  display: none;
  &:hover {
    color: #4400b3;
  }
`;


const ItemContent = styled.div.attrs({
  className: "ItemContent"
})`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${DropdownItem}:hover & + ${ActivateLink} {
    display: flex;
  }
`;


const DeleteItemButton = styled.button.attrs({
  className: "DeleteItemButton"
})`
  visibility: hidden;
  background: none;
  border: none;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  padding: 5px 4px;
  margin-left: 35px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 10px;
  ${DropdownItem}:hover & {
    visibility: visible;
  }
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #ff4d4d;
  }
`;

const EmptyState = styled.div.attrs({
  className: "EmptyState"
})`
  padding: 5px 10px;
  text-align: left;
  color: #666;
  font-size: 13px;
  border-radius: 3px;
  background: #f1f1f1;
  width: fit-content;
`;

const AddNewButton = styled.button.attrs({
  className: 'AddNewButton'
})`
  padding: 8px 10px;
  background: #f7f7f7;
  border: none;
  border-radius: 3px;
  color: #0086d0;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  width: fit-content;
  text-align: left;
  margin-bottom: 5px;
  
  &:hover {
    background: #e5e5e5;
  }
`;

const StatusDot = styled.div.attrs({
  className: 'StatusDot'
})`
  width: 11px;
  height: 11px;
  border-radius: 60%;
  background-color: ${({ type }) => {
    switch (type) {
      case "green":
        return "#4CAF50";
      case "red":
        return "#F44336";
      case "orange":
        return "#FF9800";
      default:
        return "#000000";
    }
  }};
`;

export default SelectOppRisk;
